import { Badge } from '@zoopla-eng/zds-components/badges';
import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import { RadioGroup } from '@zoopla-eng/zds-components/radio-group';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { FC, useEffect, useState } from 'react';
import {
    agentsFlagStyle,
    agentsOptionCheckBoxStyle,
    agentsOptionWrapperStyle,
    agentsRequiredTextStyle,
    agentsSelectorCounterStyle,
    agentsSelectorHeadingStyle,
    scrollableListStyle
} from './AgentsSelection.styles.css';
import { EmailAllBranchContact } from './EmailAgents.types';

type AgentProps = {
    label: string;
    subLabel?: string;
    name?: string;
    value?: number;
    featured?: boolean;
    selected?: boolean;
    toggle: (value?: number) => void;
    singleColumnLayout?: boolean;
};

type AgentsOptionsSelectorProps = {
    onChange: (branchIds: number[]) => void;
    key?: string;
    branches?: EmailAllBranchContact[];
    validationError?: boolean;
    singleColumnLayout?: boolean;
};

const AgentsOption: FC<React.PropsWithChildren<AgentProps>> = ({
    label,
    subLabel,
    name,
    value,
    featured,
    selected,
    toggle,
    singleColumnLayout = false
}) => {
    return (
        <div
            className={agentsOptionWrapperStyle({
                multiColumnLayout: !singleColumnLayout
            })}
        >
            <div
                className={agentsOptionCheckBoxStyle({
                    multiColumnLayout: !singleColumnLayout
                })}
            >
                <Checkbox
                    onClick={() => {
                        toggle(value);
                    }}
                    value={value}
                    name={name}
                    label={label}
                    helpText={subLabel}
                    checked={selected}
                />
                {featured && (
                    <div className={agentsFlagStyle}>
                        <Badge level={4} text="Featured" />
                    </div>
                )}
            </div>
        </div>
    );
};

type LocalBranches = EmailAllBranchContact & {
    checked?: boolean | null | undefined;
};

const areAllBranchesAreSelected = (localBranches: LocalBranches[]) => {
    const allSelectedBranches =
        localBranches?.filter((branch) => branch.checked) || [];

    return localBranches.length === allSelectedBranches.length;
};

const areAllFeaturedBranchesAreSelected = (
    localBranches: LocalBranches[],
    allBranchesSelected: boolean
) => {
    const featuredBranches =
        localBranches?.filter((branch) => branch.featured) || [];
    const allSelectedFeaturedBranches =
        featuredBranches?.filter((branch) => branch.checked) || [];

    return (
        featuredBranches.length === allSelectedFeaturedBranches.length &&
        !allBranchesSelected
    );
};

type RadioValue = 'select-all' | 'select-featured' | undefined;

export const AgentsOptionsSelector: FC<
    React.PropsWithChildren<AgentsOptionsSelectorProps>
> = ({
    branches = [],
    onChange,
    validationError,
    singleColumnLayout = false
}) => {
    const [localBranches, setLocalBranches] = useState(branches);
    const [selectedValue, setSelectedValue] =
        useState<RadioValue>('select-all');
    const [selectedCount, setSelectedCount] = useState(0);

    const checkedBranches = [...localBranches.map((branch) => branch.checked)];

    useEffect(() => {
        const allBranchesSelected = areAllBranchesAreSelected(localBranches);
        const allFeaturedBranchesSelected = areAllFeaturedBranchesAreSelected(
            localBranches,
            allBranchesSelected
        );

        setSelectedValue(
            allBranchesSelected
                ? 'select-all'
                : allFeaturedBranchesSelected
                ? 'select-featured'
                : undefined
        );

        onChange(
            localBranches.reduce<number[]>((selected, lb) => {
                if (lb.value && lb.checked) {
                    return [...selected, lb.value];
                }

                return selected;
            }, [])
        );
        /* eslint-disable react-hooks/exhaustive-deps */
    }, checkedBranches);

    useEffect(() => {
        const allSelectedBranches =
            localBranches?.filter((branch) => branch.checked) || [];
        setSelectedCount(allSelectedBranches.length);
    }, [localBranches]);

    const toggleSetAllSelected = () => {
        const allBranchesSelected = areAllBranchesAreSelected(localBranches);

        const selectedBranches = localBranches.map((branch) => ({
            ...branch,
            checked: !allBranchesSelected
        }));

        setLocalBranches(selectedBranches);
    };

    const toggleSetFeaturedSelected = () => {
        const allBranchesSelected = areAllBranchesAreSelected(localBranches);
        const allFeaturedBranchesSelected = areAllFeaturedBranchesAreSelected(
            localBranches,
            allBranchesSelected
        );

        const selectedBranches = localBranches.map((branch) => {
            if (branch.featured) {
                return {
                    ...branch,
                    checked: !allFeaturedBranchesSelected
                };
            }
            return {
                ...branch,
                checked: branch.featured
            };
        });

        setLocalBranches(selectedBranches);
    };

    function onValueChange(v: RadioValue) {
        if (v === 'select-all') {
            toggleSetAllSelected();
        } else {
            toggleSetFeaturedSelected();
        }
    }

    const toggleBranchSelected = (index: number) => {
        const toggleBranch = localBranches.map((branch, branchIndex) => {
            if (branchIndex === index) {
                return {
                    ...branch,
                    checked: !branch.checked
                };
            }
            return branch;
        });
        setLocalBranches(toggleBranch);
    };

    return (
        <>
            <div
                className={agentsSelectorHeadingStyle({
                    multiColumnLayout: !singleColumnLayout
                })}
                key="agents"
            >
                <p
                    className={`${agentsSelectorCounterStyle} ${fontGroups.ladle}`}
                >
                    {selectedCount} selected
                </p>
                <RadioGroup
                    id="agents-selection"
                    label="Agents selection"
                    value={selectedValue}
                    onValueChange={onValueChange}
                    bisect
                    hideLabel
                    items={[
                        {
                            id: 'agents-selection-select-all',
                            label: 'Select all',
                            value: 'select-all'
                        },
                        {
                            id: 'agents-selection-select-featured',
                            label: 'Featured',
                            value: 'select-featured'
                        }
                    ]}
                />
            </div>
            <div className={scrollableListStyle}>
                {localBranches.map((branch, index) => (
                    <AgentsOption
                        key={`branch${branch.value}`}
                        toggle={() => toggleBranchSelected(index)}
                        label={branch.label || ''}
                        subLabel={branch.telephone || ''}
                        value={branch.value || 0}
                        featured={!!branch.featured}
                        selected={!!branch.checked}
                        name={`branches-${branch.value}`}
                    />
                ))}
            </div>
            {validationError && (
                <p
                    className={`${agentsRequiredTextStyle} ${fontGroups.knife}`}
                    data-testid="agents-error"
                    role="alert"
                >
                    Please select at least 1 agent or developer
                </p>
            )}
        </>
    );
};
